<template>
  <div
    v-if="widget.name"
    :class="['aiaibot-message', {
      'with-padding': !hideMessage
    }]"
  >
    <template v-if="!hideMessage">
      <img
        v-if="widget.showIcon"
        :src="iconSrc"
        alt="Alva"
        class="alva-icon"
      />
      <component
        :is="widget.name"
        v-bind="widget"
        @scrollToEnd="$emit('scrollToEnd')"
      />
    </template>
  </div>
</template>

<script>
import { computed, watch, nextTick } from '@vue/composition-api'
import store from '@/store'
import AiaiBotHTMLMessage from './AiaiBotHTMLMessage'
import AiaiBotMediaMessage from './AiaiBotMediaMessage'
import AiaiBotButtonGroup from './AiaiBotButtonGroup'

export default {
  name: 'AiaiBotMessage',

  components: {
    AiaiBotHTMLMessage,
    AiaiBotMediaMessage,
    AiaiBotButtonGroup
  },

  props: {
    message: {
      type: Object,
      default: () => ({})
    }
  },

  setup (props, { emit }) {
    const freeTextValues = process.env.
      VUE_APP_AIAIBOT_AUTO_FREETEXT_BUTTON_VALUES
      .split(',')
      .map(value => value.toLowerCase())
    
    const isSameSender = (messageA, messageB) => {
      const a = messageA || {}
      const b = messageB || {}
      return (a.senderId || a.sender) === (b.senderId || b.sender)
    }

    const isFreeTextButton = message => (
      message?.aiaibotPayload?.widgetType === 'button' &&
      freeTextValues.includes(message?.aiaibotPayload?.text?.toLowerCase())
    )

    const payload = computed(() =>
      props.message.aiaibotPayload ||
      props.message.payload ||
      {}
    )

    const allMessages = computed(() => {
      return store.state.uistate.isArchiveView
        ? store.state.conversation?.selectedArchivedMessages
        : store.state.conversation?.conversation?.messages
    })

    const currentMessageIndex = computed(() => {
      if (!allMessages.value.length) {
        return -1
      }

      const message = props.message
      return allMessages.value.findIndex(msg => msg.msgId === message.msgId)
    })

    const hideMessage = computed(() => {
      const index = currentMessageIndex.value

      if (index === -1) {
        return false
      }

      const next = allMessages.value[index + 1]
      const isButtonsWidget = Object.keys(payload.value).includes('buttons')
      const hasFeedback = next?.aiaibotPayload?.widgetType === 'button'

      return isButtonsWidget && hasFeedback
    })

    const hideMessageHeader = computed(() => {
      const index = currentMessageIndex.value
      
      if (index === -1) {
        return false
      }

      const current = allMessages.value[index]
      const prev = allMessages.value[index - 1]
      
      if (!prev) {
        return false
      }

      return isSameSender(current, prev) || isFreeTextButton(prev)
    })

    const showIcon = computed(() => {
      const index = currentMessageIndex.value

      if (index === -1) {
        return false
      }

      const current = allMessages.value[index]
      const next = allMessages.value[index + 1]

      // use case: hidden freetext button as 2nd next
      if (
        isFreeTextButton(allMessages.value[index + 2]) &&
        Boolean(allMessages.value[index + 3])
      ) {
        return false
      }

      return !isSameSender(current, next) ||
        // use case: buttons group
        Boolean((next?.aiaibotPayload || next?.payload)?.buttons) ||
        // use case: handover
        next?.aiaibotPayload?.sessionAction === 'handover' ||
        next?.msgType === 'AIAIBOT_FINISH' ||
        // use case: intent
        next?.payloadType === 'intent' ||
        (
          next?.msgType === 'AIAIBOT' &&
          next?.aiaibotPayload &&
          Object.keys(next?.aiaibotPayload).length === 0
        ) ||
        // use case: user-input
        next?.payloadType === 'user-input' ||
        Object.keys(next?.aiaibotPayload || {}).includes('inputType') ||
        // use case: last message
        Boolean(!next)
    })

    const widget = computed(() => {      
      let widget = {
        name: undefined,
        message: '',
        hideHeader: hideMessageHeader.value,
        customerName: store.state.conversation?.user?.name,
        agentName: store.getters['conversation/getActiveAgent']?.agentName,
        isOutgoing: false,
        showIcon: false,
        ...(props.message.msgDate
          ? { date: new Date(props.message.msgDate) }
          : {}
        )
      }

      if (
        props.message.msgType === 'AIAIBOT_FEEDBACK' &&
        !freeTextValues.includes(payload.value.text.toLowerCase())
      ) {
        widget = {
          ...widget,
          name: 'AiaiBotHTMLMessage',
          message: payload.value.text,
          isOutgoing: true
        }
      } else if (
        props.message.payloadType === 'media' ||
        Object.keys(payload.value).includes('mediaType')
      ) {
        widget = {
          ...widget,
          name: 'AiaiBotMediaMessage',
          message: payload.value,
          showIcon: showIcon.value
        }
      } else if (
        props.message.payloadType === 'button-group' ||
        Object.keys(payload.value).includes('buttons')
      ) {
        widget = {
          ...widget,
          name: 'AiaiBotButtonGroup',
          options: payload.value.buttons
        }
      } else if (
        props.message.payloadType === 'html' || (
          Object.keys(payload.value).includes('content') &&
          !Object.keys(payload.value).includes('inputType')
        )
      ) {
        widget = {
          ...widget,
          name: 'AiaiBotHTMLMessage',
          message: payload.value.content,
          showIcon: showIcon.value
        }
      }

      return widget
    })

    watch(
      () => widget.value,
      async () => {
        await nextTick()
        emit('scrollToEnd')
      },
      { immediate: true }
    )

    return {
      widget,
      hideMessage,
      iconSrc: store.getters['uistate/aiaiBotIncomingIcon']
    }
  }
}
</script>

<style lang="postcss" scoped>
.aiaibot-message {
  flex: 1;
  display: flex;
  justify-content: flex-end;
  margin-left: 56px;

  & > *:nth-child(2) {
    flex: 1;
  }

  &.with-padding {
    padding: 7.5px 25px 7.5px 15px;
  }
}

.alva-icon {
  width: 40px;
  height: 40px;
  margin: 0 16px 0 -56px;
  align-self: flex-end;
  flex: none;
}
</style>